import React, { FC, useMemo } from 'react';

import { Divider, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { Box, Stack } from '@mui/system';

import {
  AllowancesNames,
  ContractTypes,
  convertToDecimals,
  currency,
  decimalMaxLength,
  getLongTermContractAttributes,
} from '@pharmaplan/common';
import { getDaysDistribution } from '@pharmaplan/common/helpers/utils';

import { useAppSelector } from '../../../../hooks/useAppSelector';
import CustomTextField from '../../../common/CustomTextField';
import { Constants, InputTypes } from '../../../../helpers/Constants';
import strings from '../../../../localization';

import classes from './styles';

interface ILongTermHourlyRates {
  formik: FormikProps<any>;
  customWeekHours?: number;
  customWeekendHours?: number;
  CustomContent?: React.ReactNode;
  bookedWeekdayCost?: number;
  bookedWeekendCost?: number;
  bookedWeekdayHours?: number;
  bookedWeekendHours?: number;
  customWeekdayLabel?: string;
  customWeekendLabel?: string;
  canEditRatesOutsideRange?: boolean;
}

const LongTermHourlyRates: FC<ILongTermHourlyRates> = ({
  formik,
  customWeekHours,
  customWeekendHours,
  CustomContent,
  bookedWeekdayCost,
  bookedWeekendCost,
  bookedWeekdayHours = 0,
  bookedWeekendHours = 0,
  customWeekdayLabel,
  customWeekendLabel,
  canEditRatesOutsideRange = false,
}) => {
  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const { startDate, endDate, shiftContractAttributes } = longTermAttributes ?? {};

  const {
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    isProfessionalPromoBooking = false,
  } = formik.values ?? {};
  const { weekdayHours, weekendHours } = useMemo(() => {
    if (
      (customWeekHours || customWeekHours === 0)
      && (customWeekendHours || customWeekendHours === 0)
    ) {
      return {
        weekdayHours: customWeekHours,
        weekendHours: customWeekendHours,
      };
    }
    return getDaysDistribution(shiftContractAttributes);
  }, [customWeekHours, customWeekendHours, shiftContractAttributes]);

  const weekdayRemove = bookedWeekdayHours * weekDayPaidHourlyRate;
  const weekendRemove = bookedWeekendHours * weekendPaidHourlyRate;

  const weekdayTotalRate = weekDayPaidHourlyRate * weekdayHours - (weekdayRemove ?? 0);
  const weekendTotalRate = weekendPaidHourlyRate * weekendHours - (weekendRemove ?? 0);
  const totalBookedCost = (bookedWeekdayCost ?? 0) + (bookedWeekendCost ?? 0);

  const total = `${currency}${convertToDecimals(
    weekdayTotalRate + weekendTotalRate + totalBookedCost,
  )} + Tx ${strings.and} ${strings.otherFees}`;

  const showCalculation = startDate && endDate;
  const showWeekday = weekDayPaidHourlyRate && weekdayHours;
  const showWeekend = weekendPaidHourlyRate && weekendHours;

  // Super admins can set a rate even when the date range has no days of that type.
  const isWeekdayRateDisabled = !weekdayHours && !canEditRatesOutsideRange;
  const isWeekendRateDisabled = !weekendHours && !canEditRatesOutsideRange;

  if (formik.values.contractType === ContractTypes.shortTerm) return null;

  return (
    <Stack>
      <Typography sx={classes.title}>{strings.rateAndAllowances}</Typography>

      <CustomTextField
        label={customWeekdayLabel ?? strings.weekdayHourlyRate}
        name={AllowancesNames.weekDayPaidHourlyRate}
        formik={formik}
        disabled={isWeekdayRateDisabled || isProfessionalPromoBooking}
        placeholder={Constants.allowancePlaceholder}
        suffix={Constants.priceSuffix}
        maxLength={decimalMaxLength}
        customType={InputTypes.number}
        customClass={classes.hourlyRate}
        id={AllowancesNames.weekDayPaidHourlyRate}
        horizontal
      />

      <CustomTextField
        label={customWeekendLabel ?? strings.weekendHourlyRate}
        name={AllowancesNames.weekendPaidHourlyRate}
        formik={formik}
        customType={InputTypes.number}
        maxLength={decimalMaxLength}
        disabled={isWeekendRateDisabled || isProfessionalPromoBooking}
        placeholder={Constants.allowancePlaceholder}
        suffix={Constants.priceSuffix}
        customClass={classes.hourlyRate}
        id={AllowancesNames.weekendPaidHourlyRate}
        horizontal
      />

      {CustomContent}

      {showCalculation && (
        <Box sx={classes.calculationContainer}>
          <Typography sx={classes.title}>
            {strings.estimatedShiftCost}
          </Typography>

          {!!showWeekday && (
            <Box sx={classes.weekContainer}>
              <Typography sx={classes.label}>{strings.weekday}</Typography>
              <Typography sx={classes.rateValue}>
                {`${currency}${weekDayPaidHourlyRate} X ${
                  weekdayHours - bookedWeekdayHours
                } = ${currency}${convertToDecimals(weekdayTotalRate)}`}
              </Typography>
            </Box>
          )}

          {!!showWeekend && (
            <Box sx={classes.weekContainer}>
              <Typography sx={classes.label}>{strings.weekend}</Typography>
              <Typography sx={classes.rateValue}>
                {`${currency}${weekendPaidHourlyRate} X ${
                  weekendHours - bookedWeekendHours
                } = ${currency}${convertToDecimals(weekendTotalRate)}`}
              </Typography>
            </Box>
          )}

          {!!totalBookedCost && (
            <Box sx={classes.weekContainer}>
              <Typography sx={classes.label}>
                {strings.bookedContractValue}
              </Typography>
              <Typography sx={classes.rateValue}>
                {`${currency}${convertToDecimals(totalBookedCost)}`}
              </Typography>
            </Box>
          )}
          <Divider sx={classes.divider} />
          <Box sx={classes.weekContainer}>
            <Typography sx={classes.label}>{strings.total}</Typography>
            <Typography sx={classes.rateValue}>{total}</Typography>
          </Box>
        </Box>
      )}
    </Stack>
  );
};
export default LongTermHourlyRates;
